﻿/** BBG market languages (ISO-style codes used in the app). */
export const BBG_LANGUAGE_CODES = [
  'DA',
  'EN',
  'DE',
  'ES',
  'FR',
  'IT',
  'NL',
  'PL',
  'PT',
  'SE',
] as const

export type BbgLanguageCode = (typeof BBG_LANGUAGE_CODES)[number]

const LEGACY_LANGUAGE_ALIASES: Record<string, BbgLanguageCode> = {
  SV: 'SE',
  sv: 'SE',
  Se: 'SE',
  se: 'SE',
  NO: 'SE',
  NB: 'SE',
  NN: 'SE',
}

export function normalizeLanguageCode(
  lang: string | null | undefined,
): BbgLanguageCode {
  const raw = String(lang ?? 'EN').trim()
  if (!raw) return 'EN'
  const upper = raw.toUpperCase()
  if (LEGACY_LANGUAGE_ALIASES[raw] || LEGACY_LANGUAGE_ALIASES[upper]) {
    return LEGACY_LANGUAGE_ALIASES[raw] ?? LEGACY_LANGUAGE_ALIASES[upper]!
  }
  if ((BBG_LANGUAGE_CODES as readonly string[]).includes(upper)) {
    return upper as BbgLanguageCode
  }
  return 'EN'
}

export function isBbgLanguageCode(lang: string): lang is BbgLanguageCode {
  return (BBG_LANGUAGE_CODES as readonly string[]).includes(lang)
}

/** Copy `SV` pack to `SE` when only legacy Swedish code exists. */
export function aliasSvToSeInRecords(
  record: Record<string, Record<string, unknown> | undefined>,
): void {
  if (record.SV && !record.SE) {
    record.SE = { ...record.SV }
  }
}

export function lookupTranslation(
  table: Record<string, Record<string, unknown> | undefined> | undefined,
  lang: string,
  key: string,
): unknown {
  if (!table) return undefined
  const code = normalizeLanguageCode(lang)
  const primary = table[code]?.[key]
  if (primary !== undefined && primary !== null && primary !== '') return primary
  const en = table.EN?.[key]
  if (en !== undefined && en !== null && en !== '') return en
  const da = table.DA?.[key]
  if (da !== undefined && da !== null && da !== '') return da
  return undefined
}

export function translateString(
  table: Record<string, Record<string, unknown> | undefined>,
  lang: string,
  key: string,
): string {
  const value = lookupTranslation(table, lang, key)
  if (typeof value === 'string') return value
  if (value == null) return key
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return key
}

/** Swedish UI strings for workspace chrome (nav, dashboard, library). */
export const SE_GLOBAL_UI_OVERRIDES: Record<string, string> = {
  lang_speakers: 'talare',
  workspace_title: 'Bokarbetsyta',
  nav_overview: 'Översikt',
  nav_interview: 'LITRA!',
  nav_write: 'Skriv & redigera',
  nav_proofread: 'Korrekturläsning',
  nav_cover: 'Omslagsdesign',
  nav_branding: 'Marknadsföringskit',
  nav_publish_export: 'Publicera & exportera',
  nav_publish_guide: 'Publiceringsguide',
  nav_library: 'Mitt bibliotek',
  nav_monetize: 'Tjäna pengar',
  nav_group_writing: 'Skrivprocess',
  nav_group_marketing: 'Marknadsföring',
  nav_group_publishing: 'Publicering',
  nav_group_documents: 'Dokument',
  nav_group_monetize: 'Tjäna pengar',
  nav_status_done: 'Klar',
  nav_status_pending: 'Väntar',
  coming_title: 'Kommer i nästa fas',
  coming_body: 'Detta arbetsområde är reserverat för nästa implementationsfas.',
  dashboard_hello: 'Hej',
  dashboard_wave: '👋',
  dashboard_progress: 'Framsteg',
  dashboard_next: 'Nästa steg',
  dashboard_continue: 'Fortsätt',
  dashboard_recent: 'Senaste filer',
  dashboard_help: 'Behöver du hjälp?',
  dashboard_help_body:
    'Öppna en modul i menyn, eller fortsätt med nästa väntande steg.',
  dashboard_no_files: 'Inga filer ännu',
  dashboard_step_interview: 'LITRA!',
  dashboard_step_write: 'Skriv',
  dashboard_step_proof: 'Korrektur',
  dashboard_step_cover: 'Omslag',
  library_title: 'Mitt bibliotek',
  library_sub: 'Dina lokalt sparade bokprojekt.',
  library_save: 'Spara aktuell bok',
  library_new: 'Ny bok',
  library_empty_title: 'Inga sparade böcker ännu',
  library_empty_body: 'Spara din aktuella bok för att se den här.',
  library_load: 'Ladda',
  library_delete: 'Radera',
  library_words: 'ord',
  library_updated: 'Uppdaterad',
  library_current: 'Aktuell',
  library_saved: 'Sparat',
  settings_title: 'Konto & inställningar',
  settings_profile: 'Profil',
  settings_security: 'Säkerhet',
  settings_subscription: 'Prenumeration',
  settings_notifications: 'Notifikationer',
  settings_language: 'Språk & tillgänglighet',
  settings_privacy: 'Integritet & data',
  settings_support: 'Support',
  settings_logout: 'Logga ut',
}

export const IT_GLOBAL_UI_OVERRIDES: Record<string, string> = {
  lang_speakers: 'parlanti',
  workspace_title: 'Area libro',
  nav_overview: 'Panoramica',
  nav_interview: 'LITRA!',
  nav_write: 'Scrivi e modifica',
  nav_proofread: 'Correzione',
  nav_cover: 'Design copertina',
  nav_branding: 'Kit marketing',
  nav_publish_export: 'Pubblica ed esporta',
  nav_publish_guide: 'Guida alla pubblicazione',
  nav_library: 'La mia libreria',
  nav_monetize: 'Monetizza',
  nav_group_writing: 'Processo di scrittura',
  nav_group_marketing: 'Marketing',
  nav_group_publishing: 'Pubblicazione',
  nav_group_documents: 'Documenti',
  nav_group_monetize: 'Monetizza',
  nav_status_done: 'Completato',
  nav_status_pending: 'In sospeso',
  coming_title: "In arrivo nella prossima fase",
  coming_body: "Questa area di lavoro è riservata per la prossima fase di implementazione.",
  dashboard_hello: 'Ciao',
  dashboard_wave: '👋',
  dashboard_progress: 'Progresso',
  dashboard_next: 'Prossimo passo',
  dashboard_continue: 'Continua',
  dashboard_recent: 'File recenti',
  dashboard_help: 'Ti serve aiuto?',
  dashboard_help_body: 'Apri un modulo nella barra laterale o continua con il prossimo passo.',
  dashboard_no_files: 'Nessun file',
  dashboard_step_interview: 'LITRA!',
  dashboard_step_write: 'Scrittura',
  dashboard_step_proof: 'Correzione',
  dashboard_step_cover: 'Copertina',
  library_title: 'La mia libreria',
  library_sub: 'I tuoi progetti libro salvati in locale.',
  library_save: 'Salva libro corrente',
  library_new: 'Nuovo libro',
  library_empty_title: 'Nessun libro salvato',
  library_empty_body: 'Salva il libro corrente per vederlo qui.',
  library_load: 'Carica',
  library_delete: 'Elimina',
  library_words: 'parole',
  library_updated: 'Aggiornato',
  library_current: 'Attuale',
  library_saved: 'Salvato',
  settings_title: 'Account e impostazioni',
  settings_profile: 'Profilo',
  settings_security: 'Sicurezza',
  settings_subscription: 'Abbonamento',
  settings_notifications: 'Notifiche',
  settings_language: 'Lingua e accessibilità',
  settings_privacy: 'Privacy e dati',
  settings_support: 'Supporto',
  settings_logout: 'Esci',
}

export const NL_GLOBAL_UI_OVERRIDES: Record<string, string> = {
  lang_speakers: 'sprekers',
  workspace_title: 'Boekwerkruimte',
  nav_overview: 'Overzicht',
  nav_interview: 'LITRA!',
  nav_write: 'Schrijven & bewerken',
  nav_proofread: 'Proeflezen',
  nav_cover: 'Omslagontwerp',
  nav_branding: 'Marketingkit',
  nav_publish_export: 'Publiceren & exporteren',
  nav_publish_guide: 'Publicatiegids',
  nav_library: 'Mijn bibliotheek',
  nav_monetize: 'Verdienen',
  nav_group_writing: 'Schrijfproces',
  nav_group_marketing: 'Marketing',
  nav_group_publishing: 'Publiceren',
  nav_group_documents: 'Documenten',
  nav_group_monetize: 'Verdienen',
  nav_status_done: 'Klaar',
  nav_status_pending: 'In behandeling',
  coming_title: "Komt in de volgende fase",
  coming_body: "Dit werkgebied is gereserveerd voor de volgende implementatiefase.",
  dashboard_hello: 'Hallo',
  dashboard_wave: '👋',
  dashboard_progress: 'Voortgang',
  dashboard_next: 'Volgende stap',
  dashboard_continue: 'Doorgaan',
  dashboard_recent: 'Recente bestanden',
  dashboard_help: 'Hulp nodig?',
  dashboard_help_body: 'Open een module in de zijbalk of ga door met de volgende stap.',
  dashboard_no_files: 'Nog geen bestanden',
  dashboard_step_interview: 'LITRA!',
  dashboard_step_write: 'Schrijven',
  dashboard_step_proof: 'Proeflezen',
  dashboard_step_cover: 'Omslag',
  library_title: 'Mijn bibliotheek',
  library_sub: 'Je lokaal opgeslagen boekprojecten.',
  library_save: 'Huidig boek opslaan',
  library_new: 'Nieuw boek',
  library_empty_title: 'Nog geen opgeslagen boeken',
  library_empty_body: 'Sla je huidige boek op om het hier te zien.',
  library_load: 'Laden',
  library_delete: 'Verwijderen',
  library_words: 'woorden',
  library_updated: 'Bijgewerkt',
  library_current: 'Huidig',
  library_saved: 'Opgeslagen',
  settings_title: 'Account & instellingen',
  settings_profile: 'Profiel',
  settings_security: 'Beveiliging',
  settings_subscription: 'Abonnement',
  settings_notifications: 'Meldingen',
  settings_language: 'Taal & toegankelijkheid',
  settings_privacy: 'Privacy & data',
  settings_support: 'Support',
  settings_logout: 'Uitloggen',
}

export function mergeTranslationLayers<T extends Record<string, unknown>>(
  base: T,
  ...layers: Array<Partial<T> | undefined>
): T {
  return layers.reduce(
    (acc, layer) => (layer ? { ...acc, ...layer } : acc),
    { ...base },
  ) as T
}

/** Nested copy tables: selected language → EN → DA. */
export function pickCopyRecord<T>(
  table: Record<string, T | undefined>,
  lang: string,
): T {
  const code = normalizeLanguageCode(lang)
  const primary = table[code]
  if (primary !== undefined) return primary
  const en = table.EN
  if (en !== undefined) return en
  const da = table.DA
  if (da !== undefined) return da
  const first = Object.values(table).find((v) => v !== undefined)
  if (first === undefined) {
    throw new Error('pickCopyRecord: empty table')
  }
  return first
}

const EXPLICIT_LANGUAGE_PATTERNS: Array<{ code: BbgLanguageCode; re: RegExp }> = [
  { code: 'DA', re: /\b(dansk|danish|på dansk|in danish)\b/i },
  { code: 'EN', re: /\b(english|engelsk|på engelsk|in english)\b/i },
  { code: 'DE', re: /\b(german|deutsch|tysk|auf deutsch|på tyska)\b/i },
  { code: 'ES', re: /\b(spanish|español|spansk|en español|på spanska)\b/i },
  { code: 'FR', re: /\b(french|français|fransk|en français|på franska)\b/i },
  { code: 'IT', re: /\b(italian|italiano|italiensk|in italiano|på italienska)\b/i },
  { code: 'NL', re: /\b(dutch|nederlands|hollandsk|in dutch|på nederländska)\b/i },
  { code: 'PT', re: /\b(portuguese|português|portugisisk|em português|på portugisiska)\b/i },
  { code: 'PL', re: /\b(polish|polski|polsk|po polsku|på polska)\b/i },
  { code: 'SE', re: /\b(swedish|svenska|svensk|på svenska|in swedish)\b/i },
]

/** Word/phrase signals for inferring language from user message text. */
const MESSAGE_LANGUAGE_SIGNALS: Partial<Record<BbgLanguageCode, RegExp[]>> = {
  SE: [
    /\b(jag|är|inte|och|vad|hur|ska|vill|berättelse|författare|kapitel|börja|fortsätta|tack|hejsan|skriva|boken|berätta)\b/gi,
    /\b(något|någon|några|gärna|också|kanske|redan|alltid|varför|därför)\b/gi,
  ],
  DA: [
    /\b(jeg|er|ikke|og|hvad|hvordan|skal|vil|historie|forfatter|kapitel|begynde|fortsætte|tak|hej|skrive|bogen|fortælle)\b/gi,
    /\b(noget|nogen|nogle|gerne|også|måske|allerede|altid|hvorfor|derfor)\b/gi,
  ],
  DE: [/\b(ich|und|nicht|was|wie|soll|will|geschichte|autor|kapitel|buch|schreiben)\b/gi],
  ES: [/\b(yo|qué|cómo|historia|autor|capítulo|libro|escribir|gracias|hola)\b/gi],
  FR: [/\b(je|quoi|comment|histoire|auteur|chapitre|livre|écrire|merci|bonjour)\b/gi],
  IT: [/\b(io|cosa|come|storia|autore|capitolo|libro|scrivere|grazie|ciao)\b/gi],
  NL: [/\b(ik|wat|hoe|verhaal|auteur|hoofdstuk|boek|schrijven|bedankt|hallo)\b/gi],
  PT: [/\b(eu|que|como|história|autor|capítulo|livro|escrever|obrigado|olá)\b/gi],
  PL: [/\b(ja|co|jak|historia|autor|rozdział|książka|pisać|dziękuję|cześć)\b/gi],
}

function scoreMessageLanguage(text: string): Partial<Record<BbgLanguageCode, number>> {
  const scores: Partial<Record<BbgLanguageCode, number>> = {}
  for (const [code, patterns] of Object.entries(MESSAGE_LANGUAGE_SIGNALS) as Array<
    [BbgLanguageCode, RegExp[]]
  >) {
    let total = 0
    for (const pattern of patterns) {
      const matches = text.match(pattern)
      if (matches) total += matches.length
    }
    if (total > 0) scores[code] = total
  }
  return scores
}

/**
 * Language for LITRA replies: explicit request → message inference → app UI language.
 * Fallback for missing copy remains selected → EN → DA (never DA before EN for SE).
 */
export function detectLiteraResponseLanguage(
  message: string,
  appLanguage: string,
): BbgLanguageCode {
  const appCode = normalizeLanguageCode(appLanguage)
  const normalized = message.trim()
  if (!normalized) return appCode

  const lower = normalized.toLowerCase()
  for (const { code, re } of EXPLICIT_LANGUAGE_PATTERNS) {
    if (re.test(lower)) return code
  }

  const scores = scoreMessageLanguage(lower)
  const ranked = (Object.entries(scores) as Array<[BbgLanguageCode, number]>).sort(
    (a, b) => b[1] - a[1],
  )

  if (ranked.length === 0) return appCode

  const [topCode, topScore] = ranked[0]
  const secondScore = ranked[1]?.[1] ?? 0

  if (appCode === 'SE' && (scores.SE ?? 0) >= Math.max(1, topScore - 1)) {
    return 'SE'
  }
  if (appCode === 'DA' && (scores.DA ?? 0) >= Math.max(1, topScore - 1)) {
    return 'DA'
  }

  if (topScore >= 2 && topScore > secondScore) return topCode
  return appCode
}

/** Full Swedish strings for the continuity bible sidebar. */
export const SE_BIBLE_SIDEBAR: Record<string, string | Record<string, string>> & {
  catLabels: Record<string, string>
} = {
  title: 'Bibel',
  characters: 'PERSONER',
  timeline: 'TIDSLINJE',
  locations: 'PLATSER',
  facts: 'FAKTA',
  noChars: 'Inga personer ännu',
  noTimeline: 'Inga händelser ännu',
  noLocations: 'Inga platser ännu',
  noFacts: 'Inga fakta ännu',
  addChar: '+ Person',
  addEvent: '+ Händelse',
  addLoc: '+ Plats',
  addFact: '+ Faktum',
  name: 'Namn',
  ageAtStart: 'Ålder vid start',
  years: 'år',
  traits: 'Personlighet (komma)',
  want: 'Vill (yttre mål)',
  need: 'Behov (inre)',
  wantShort: 'Vill',
  needShort: 'Behov',
  background: 'Bakgrund',
  alive: 'Lever',
  dead: 'Avliden',
  location: 'Plats',
  date: 'Datum / år',
  event: 'Händelse',
  involved: 'Inblandade (komma)',
  description: 'Beskrivning',
  visual: 'Visuellt',
  sounds: 'Ljud',
  smells: 'Dofter',
  statement: 'Påstående',
  category: 'Kategori',
  chapter: 'Källkapitel',
  save: 'Spara',
  cancel: 'Avbryt',
  edit: 'Redigera',
  delete: 'Radera',
  chapter_updated: 'Uppdaterad efter kap.',
  catLabels: {
    'world-rule': 'Världsregel',
    'character-fact': 'Karaktärsfakta',
    'plot-event': 'Händelse i handlingen',
    'social-context': 'Sammanhang',
    knowledge: 'Kunskap',
    other: 'Annat',
  },
}
